import {VcmpClient} from "@variocube/vcmp";
import {autorun, computed, makeObservable, observable} from "mobx";
import {tokenStore} from "../token";
import {
	BoxAvailabilityResponse,
	CubeResponse,
	Location,
	LocationCodeShape,
	OccupancyResponse,
	TraceEvent,
} from "../types";
import {KeyCardEventType, WebSocketBaseUrl} from "./keyCardEvents";
import {getJsonResult, RestClient} from "./rest";

type ConnectionEventListener = {
	onOccupancyCreated: (occupancy: OccupancyResponse) => void;
	onOccupancyUpdate: (occupancy: OccupancyResponse) => void;
};

async function connect(token: string) {
	const restClient = new RestClient(token);
	try {
		// Try to fetch account info
		const response = await restClient.get("/account");

		// If we get an Unauthorized status, we are dealing with an invalid token
		// and need to clear it
		if (response.status == 401) {
			return "InvalidToken";
		}

		// other non-OK responses can also be treated as network error
		if (!response.ok) {
			return "NetworkError";
		}

		return restClient;
	}
	catch (error) {
		return "NetworkError";
	}
}

export enum ConnectionState {
	pending = "pending",
	online = "online",
	offline = "offline",
}

class Connection {
	private restClient?: RestClient;
	private keyCardClient?: VcmpClient;
	private listener: ConnectionEventListener[] = [];

	private state: ConnectionState = ConnectionState.offline;

	constructor() {
		makeObservable<Connection, "state">(this, {
			state: observable,
			pending: computed,
			online: computed,
			offline: computed,
		});

		window.addEventListener("online", () => this.connect());
		window.addEventListener("offline", () => this.connect());
		this.connect().then();

		autorun(() => console.log("connection", this.state));
	}

	async connect() {
		if (tokenStore.token) {
			this.state = ConnectionState.pending;
			const result = await connect(tokenStore.token);

			if (result === "InvalidToken") {
				tokenStore.clearToken();
			}
			else if (result === "NetworkError") {
				this.state = ConnectionState.offline;
			}
			else {
				this.restClient = result;

				this.keyCardClient = new VcmpClient(WebSocketBaseUrl + "/api/keycard/v2/vcmp/" + tokenStore.token);
				this.keyCardClient.onOpen = this.handleSocketConnected;
				this.keyCardClient.onClose = this.handleSocketDisconnected;
				this.keyCardClient.on(
					KeyCardEventType.KeyCardOccupancyCreated,
					(ev: { occupancy: OccupancyResponse }) => this.handleOccupancyCreated(ev.occupancy),
				);
				this.keyCardClient.on(
					KeyCardEventType.KeyCardOccupancyUpdated,
					(ev: { occupancy: OccupancyResponse }) => this.handleOccupancyUpdated(ev.occupancy),
				);

				this.keyCardClient.start();
			}
		}
	}

	get pending() {
		return this.state == ConnectionState.pending;
	}

	get online() {
		return this.state == ConnectionState.online;
	}

	get offline() {
		return this.state == ConnectionState.offline;
	}

	private requireRestClient() {
		if (this.restClient) {
			return this.restClient;
		}
		throw new Error("No token.");
	}

	async fetchOccupancies() {
		const response = await this.requireRestClient().get("/occupancies");
		return await getJsonResult<OccupancyResponse[]>(response);
	}

	async fetchCubes() {
		const response = await this.requireRestClient().get("/cubes");
		return await getJsonResult<CubeResponse[]>(response);
	}

	async fetchBoxAvailability(hostname: string) {
		const response = await this.requireRestClient().get(`/cubes/${hostname}/boxes`);
		return await getJsonResult<BoxAvailabilityResponse[]>(response);
	}

	async occupy(hostname: string, boxSize?: string) {
		const response = await this.requireRestClient().post("/occupancies", {
			hostname,
			boxSize: boxSize,
		});
		if (response.status == 507) {
			throw new InsufficientStorageError();
		}
		else if (!response.ok) {
			throw new Error("Http status not okay.");
		}
	}

	async open(occupancyId: number) {
		const response = await this.requireRestClient().post(`/occupancies/${occupancyId}/open`, undefined);
		return await getJsonResult<OccupancyResponse>(response);
	}

	async release(occupancyId: number, immediate: boolean) {
		const response = await this.requireRestClient().delete(`/occupancies/${occupancyId}`, {
			immediateRelease: immediate,
		});
		if (!response.ok) {
			throw new Error("Http status not okay.");
		}
	}

	computeCubeCenterBaseUrl() {
		let baseUrl;
		if (window.location.hostname.indexOf("safecube.io") > -1) {
			if (window.location.hostname.indexOf("dev.") > -1) {
				baseUrl = "https://api-dev.cubeadmin.center";
			}
			else {
				baseUrl = "https://api.cubeadmin.center";
			}
		}
		else {
			baseUrl = "http://localhost:7000";
		}

		return baseUrl;
	}

	async addCubeByLocation(coords: GeolocationCoordinates) {
		console.info("connection.addCubeByLocation", coords);

		// the coordinates object does not have enumerable properties,
		// so we have to create a compatible object by hand
		const customCoords = {
			latitude: coords.latitude,
			longitude: coords.longitude,
			altitude: coords.altitude,
			accuracy: coords.accuracy,
			altitudeAccuracy: coords.altitudeAccuracy,
			speed: coords.speed,
			heading: coords.heading,
		};

		const response = await window.fetch(this.computeCubeCenterBaseUrl() + "/api/location/coordinates", {
			method: "post",
			body: JSON.stringify(customCoords),
			headers: {
				"Content-type": "application/json",
			},
		});
		const locations = await getJsonResult<Location[]>(response);
		console.log("connection.addCubeByLocation: Retrieved the following locations: ", locations);

		if (locations.length === 0) {
			throw new Error("error: no cube was found at the specified location");
		}
		else if (locations.length > 1) {
			throw new Error("error: multiple cube was found at the specified location");
		}

		return this.getCubeByHostname(locations[0].hostname);
	}

	async getLocationCodeShape(): Promise<LocationCodeShape> {
		const response = await window.fetch(this.computeCubeCenterBaseUrl() + "/api/location/code");
		return getJsonResult(response);
	}

	async getCubeByCode(code: string): Promise<CubeResponse> {
		const response = await window.fetch(this.computeCubeCenterBaseUrl() + "/api/location/code/" + code);
		const location = await getJsonResult<Location>(response);
		return this.getCubeByHostname(location.hostname);
	}

	async getCubeByHostname(hostname: string): Promise<CubeResponse> {
		const response = await this.requireRestClient().post(`/cubes`, {hostname});
		return getJsonResult(response);
	}

	async removeCube(hostname: string) {
		const response = await this.requireRestClient().delete(`/cubes/${hostname}`);
		return await getJsonResult<CubeResponse[]>(response);
	}

	async trace(traceEvents: TraceEvent[]) {
		return await this.requireRestClient().post(`/trace`, traceEvents);
	}

	// handleSocketError = () => {
	//     console.log('handleSocketError');
	//     this.connect();
	// };

	handleSocketConnected = () => {
		console.log("handleSocketConnected");
		this.state = ConnectionState.online;
	};

	handleSocketDisconnected = () => {
		console.log("handleSocketDisconnected");
		this.state = ConnectionState.offline;
	};

	// handleCubesChange = (cubes: CubeResponse[]) => {
	//     console.log('handleCubesChange', cubes);
	//     for (let listener of this.listener) {
	//         if (listener.onCubesChange) {
	//             try {
	//                 listener.onCubesChange(cubes);
	//             } catch (error) {
	//                 console.error('Error thrown from listener', error);
	//             }
	//         }
	//     }
	// };

	handleOccupancyCreated = (occupancy: OccupancyResponse) => {
		console.log("handleOccupancyCreated", occupancy);
		for (let listener of this.listener) {
			if (listener.onOccupancyCreated) {
				try {
					listener.onOccupancyCreated(occupancy);
				}
				catch (error) {
					console.error("Error thrown from listener", error);
				}
			}
		}
	};

	handleOccupancyUpdated = (occupancy: OccupancyResponse) => {
		console.log("handleOccupancyUpdate", occupancy);
		for (let listener of this.listener) {
			if (listener.onOccupancyUpdate) {
				try {
					listener.onOccupancyUpdate(occupancy);
				}
				catch (error) {
					console.error("Error thrown from listener", error);
				}
			}
		}
	};

	addEventListener(listener: ConnectionEventListener) {
		this.listener.push(listener);
	}
}

export class InsufficientStorageError {
}

export const connection = new Connection();
